// The one place a screen wires a state-changing action to its three
// feedback layers. `confirm` is the shared promise-based dialog (use it for
// irreversible / financial actions before firing the mutation); `toastOk`
// and `toastErr` are the success/failure toasts. Cache invalidation so the
// screen reflects the new state stays in each mutation hook's own
// `onSuccess` (react-query) — this helper is only about the human feedback.
//
//   const { confirm, toastOk, toastErr } = useActionFeedback();
//   ...
//   if (!(await confirm({ title, message, tone: 'danger' }))) return;
//   mutation.mutate(payload, {
//     onSuccess: () => toastOk(t('screen.doneToast')),
//     onError: (err) => toastErr(err, ['KNOWN_CODE']),
//   });

import { useCallback } from 'react';
import { useToast, useConfirm } from 'shared/components/Feedback/index.js';
import { ApiError } from 'shared/api/client.js';
import { useLanguage } from '../context/LanguageContext.jsx';

export function useActionFeedback() {
  const toast = useToast();
  const confirm = useConfirm();
  const { t, lang } = useLanguage();

  const toastOk = useCallback(
    (message) => toast.success(message || t('feedback.saved')),
    [toast, t]
  );

  // `knownCodes` is the small allow-list of error codes this action can
  // meaningfully report (same discipline as portal's mapApiErrorId). Never
  // renders a raw server message (root CLAUDE.md).
  const toastErr = useCallback(
    (err, knownCodes = []) => {
      let id = 'feedback.actionFailed';
      let showCode = false;
      if (err instanceof ApiError) {
        if (err.status === 0) id = 'error.NETWORK_ERROR';
        else if (knownCodes.includes(err.code)) id = `error.${err.code}`;
        else {
          // No specific message for this code — name the code in the toast
          // so the failure is identifiable without opening devtools (the
          // full response is already logged there by shared/api/client.js).
          id = 'error.INTERNAL_ERROR';
          showCode = Boolean(err.code);
        }
      }
      toast.error(showCode ? `${t(id)} (${err.code})` : t(id));
    },
    [toast, t]
  );

  return { t, lang, toast, confirm, toastOk, toastErr };
}

export default useActionFeedback;
